import { KeyboardEvent, memo, useCallback, useContext, useMemo } from "react";
import { ChatFooterContainer } from "./components/chat-footer-container";
import { ChatFooterInputGroup } from "./components/chat-footer-input-group";
import { ChatFooterInput } from "./components/chat-footer-input";
import { ChatFooterSendButton } from "./components/chat-footer-send-button";
import { useTheme } from "styled-components";
import { ChatContext } from "../../contexts/chat-context";
import { ChatFooterFootnotes } from "./components/chat-footer-footnotes";
import { useAppSelector } from "../../../common/store";
import { chatSelector } from "../../../common/store/chat/selectors";
import { SendIcon } from "../../components/icons/send-icon";
import useMaxChar from "../../hooks/use-max-chars";
import WarningMessage from "./warning-message";
import AlertCircle from "../../components/icons/alert-icon";

const ChatFooter = () => {
  const theme = useTheme();
  const storeMessages = useAppSelector(chatSelector);
  const { sendUserInput, userTyping, forceStopUserTyping } = useContext(ChatContext);

  const {
    handleInputOnChange, inputValue, setInputValue, warning, isReachedLimit,
  } = useMaxChar();

  const isWaitingAnimationOrResponse = useMemo(
    () => storeMessages.isStreamingHandling || !storeMessages.isDoneAnimationBuffer,
    [storeMessages.isStreamingHandling, storeMessages.isDoneAnimationBuffer],
  );

  const handleSendButtonClick = useCallback(() => {
    if (!sendUserInput || isWaitingAnimationOrResponse) return;

    // force-stop typing before sending
    forceStopUserTyping();

    sendUserInput(inputValue);
    setInputValue(() => '');
  }, [sendUserInput, inputValue, isWaitingAnimationOrResponse, forceStopUserTyping, setInputValue]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      // Every key press should trigger the typing handler
      userTyping();

      if (e.key === 'Enter') {
        e.preventDefault();
        // force-stop typing before send
        forceStopUserTyping();
        handleSendButtonClick();
      }
    },
    [handleSendButtonClick, userTyping, forceStopUserTyping]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // original onChange logic
      handleInputOnChange(e);
      // Trigger typing handler on change
      userTyping();

      // If input becomes empty after change (e.g., backspace/delete all), force stop typing
      if (e.target.value.trim() === '') {
        forceStopUserTyping();
      }
    },
    [handleInputOnChange, userTyping, forceStopUserTyping]
  );

  return (
    <ChatFooterContainer className="voa_chat_footer">
      <ChatFooterInputGroup isValid={!isReachedLimit} className="voa_user_input_group">
        <ChatFooterInput
          className="voa_user_input"
          placeholder={theme.themeConfig?.chatFooter?.inputGroup?.input?.placeholder}
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <ChatFooterSendButton
          onClick={handleSendButtonClick}
          disabled={isWaitingAnimationOrResponse || !inputValue.trim()} // Disable if input is empty
          className="voa_user_input_confirm_button"
        >
          <SendIcon />
        </ChatFooterSendButton>
        {warning && (
          <WarningMessage>
            <AlertCircle />
            <span>{warning}</span>
          </WarningMessage>
        )}
      </ChatFooterInputGroup>
      {theme.themeConfig?.chatFooter?.footnotes?.innerHtml &&
        <ChatFooterFootnotes className="voa_footer_footnotes" dangerouslySetInnerHTML={{ __html: theme.themeConfig?.chatFooter?.footnotes?.innerHtml }}/>
      }
    </ChatFooterContainer>
  )
};

export default memo(ChatFooter);
